import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { acceptanceLetterHtml, receiptHtml, openPrintable } from "@/lib/docs";
import {
  Loader2, UploadCloud, FileText, Calendar, Hash, Download, MessagesSquare,
  FilePenLine, Receipt, Award, Eye, XCircle, Star, FileCheck, Copy,
} from "lucide-react";

const TABS = [
  { k: "all", l: "All" },
  { k: "submitted", l: "Submitted" },
  { k: "under_review", l: "Under Review" },
  { k: "revision_required", l: "Revision" },
  { k: "accepted", l: "Accepted" },
  { k: "proofreading", l: "Proof-reading" },
  { k: "published", l: "Published" },
  { k: "closed", l: "Rejected / Withdrawn" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

function ReviewsDialog({ paper }) {
  const [reviews, setReviews] = useState(null);
  const load = () => api.get(`/my/papers/${paper.id}/reviews`).then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`reviews-btn-${paper.id}`}><MessagesSquare className="w-3.5 h-3.5" /> Reviewer Comments</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-left">Reviewer Comments</DialogTitle><DialogDescription>Anonymised feedback from the peer reviewers assigned to your manuscript.</DialogDescription></DialogHeader>
        {reviews === null ? (
          <div className="py-8 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : reviews.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">No completed reviews are available yet.</p>
        ) : (
          <div className="space-y-3">
            {reviews.map((r, i) => (
              <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-800 p-3" data-testid={`review-block-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{r.label}</span>
                  <span className="flex items-center gap-0.5 text-amber-500 text-xs">
                    {Array.from({ length: 5 }).map((_, n) => <Star key={n} className={`w-3.5 h-3.5 ${n < (r.rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
                  </span>
                </div>
                <p className="text-xs capitalize text-primary dark:text-sky-400 mb-1">{(r.recommendation || "").replace("_", " ")}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">{r.comments}</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RevisionDialog({ paper, reload }) {
  const [text, setText] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileUrl(`${BACKEND}${data.url}`); setFileName(data.filename);
      toast.success("Revised file uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setUploading(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/my/papers/${paper.id}/revise`, { file_url: fileUrl, file_name: fileName, manuscript_text: text || null });
      toast.success("Revision resubmitted for review");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5 border-orange-300 text-orange-700 dark:text-orange-300" data-testid={`revise-btn-${paper.id}`}><FilePenLine className="w-3.5 h-3.5" /> Revision</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-left">Submit a Revision</DialogTitle><DialogDescription>Upload your revised manuscript and an optional response to the reviewers.</DialogDescription></DialogHeader>
        {paper.decision_note && (
          <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 p-3 text-sm text-orange-800 dark:text-orange-200" data-testid={`revise-editor-note-${paper.id}`}>
            <p className="font-semibold mb-1">Editor's revision request</p>{paper.decision_note}
          </div>
        )}
        <div>
          <Label>Upload revised manuscript</Label>
          <label className="mt-1.5 flex items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 p-3 cursor-pointer text-sm text-slate-500">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {fileName || "Choose PDF/DOCX"}
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFile} data-testid={`revise-file-${paper.id}`} />
          </label>
        </div>
        <div>
          <Label>Response to reviewers (optional)</Label>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="mt-1.5" placeholder="Summary of changes…" data-testid={`revise-note-${paper.id}`} />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid={`revise-submit-${paper.id}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resubmit Revision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubmissionCard({ paper, reload }) {
  const accepted = ["accepted", "published"].includes(paper.status);
  const canWithdraw = !["published", "rejected", "withdrawn"].includes(paper.status);
  const hasReviews = ["under_review", "revision_required", "accepted", "rejected", "published"].includes(paper.status);
  const [revUploading, setRevUploading] = useState(false);

  const withdraw = async () => {
    if (!window.confirm("Withdraw this submission? This cannot be undone.")) return;
    try { await api.post(`/my/papers/${paper.id}/withdraw`); toast.success("Submission withdrawn"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveProof = async () => {
    try { await api.post(`/my/papers/${paper.id}/approve-proof`); toast.success("Proof approved — thank you!"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadRevised = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRevUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/my/papers/${paper.id}/proof-file`, { url: data.url, name: data.filename });
      toast.success("Revised proof uploaded");
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setRevUploading(false); e.target.value = ""; }
  };

  return (
    <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid={`my-paper-${paper.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap"><StatusBadge status={paper.status} />{accepted && <Award className="w-4 h-4 text-emerald-600" />}</div>
          <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100">{paper.title}</h3>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
            <span className="inline-flex items-center gap-1 font-mono text-amber-600" data-testid={`sub-id-${paper.id}`}><Hash className="w-3.5 h-3.5" /> {paper.submission_id || "—"}</span>
            {paper.submission_id && (
              <button type="button" onClick={() => { navigator.clipboard?.writeText(paper.submission_id); toast.success("Paper ID copied"); }} className="inline-flex items-center gap-1 text-slate-400 hover:text-primary transition-colors" title="Copy Paper ID" data-testid={`copy-sub-id-${paper.id}`}><Copy className="w-3.5 h-3.5" /></button>
            )}
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Submitted {fmtDate(paper.created_at)}</span>
            <span className="italic">{paper.journal?.title}</span>
          </div>
        </div>
      </div>

      {paper.status === "proofreading" && (
        <div className="mt-4 rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 space-y-3" data-testid={`proof-review-${paper.id}`}>
          <p className="font-semibold text-sm text-indigo-800 dark:text-indigo-200 flex items-center gap-1.5"><FileCheck className="w-4 h-4" /> Proof ready for your approval</p>
          {paper.proof_notes && (
            <div className="text-sm text-slate-700 dark:text-slate-300" data-testid={`proof-notes-view-${paper.id}`}>
              <span className="font-medium">Editor's notes: </span>{paper.proof_notes}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {paper.file_url && (
              <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`view-proof-${paper.id}`} onClick={() => window.open(`${BACKEND}${paper.file_url}`, "_blank")}>
                <FileText className="w-3.5 h-3.5" /> View Proof File
              </Button>
            )}
            <label className="inline-flex items-center gap-1.5 text-sm rounded-full border border-slate-200 dark:border-slate-700 px-4 py-1.5 cursor-pointer hover:border-primary hover:text-primary transition-colors" data-testid={`upload-revised-${paper.id}`}>
              {revUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />} Upload Revised File
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadRevised} />
            </label>
            {paper.proof_approved ? (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium" data-testid={`proof-approved-${paper.id}`}>✓ You approved this proof</span>
            ) : (
              <Button size="sm" className="rounded-full gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={approveProof} data-testid={`approve-proof-${paper.id}`}>
                <FileCheck className="w-3.5 h-3.5" /> Approve Proof
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
        {paper.file_url && (
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`download-ms-${paper.id}`} onClick={() => window.open(paper.file_url, "_blank")}>
            <Download className="w-3.5 h-3.5" /> Download Manuscript
          </Button>
        )}
        {hasReviews && <ReviewsDialog paper={paper} />}
        {paper.status === "revision_required" && <RevisionDialog paper={paper} reload={reload} />}
        {accepted && (
          <>
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`acceptance-${paper.id}`} onClick={() => openPrintable("Acceptance Letter", acceptanceLetterHtml(paper))}>
              <Award className="w-3.5 h-3.5" /> Acceptance Letter
            </Button>
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`receipt-${paper.id}`} onClick={() => openPrintable("Payment Receipt", receiptHtml(paper))}>
              <Receipt className="w-3.5 h-3.5" /> Payment Receipt
            </Button>
          </>
        )}
        {paper.status === "published" && (
          <Button asChild variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`view-published-${paper.id}`}>
            <Link to={`/paper/${paper.id}`}><Eye className="w-3.5 h-3.5" /> View Published</Link>
          </Button>
        )}
        {canWithdraw && (
          <Button variant="ghost" size="sm" className="rounded-full gap-1.5 text-destructive" data-testid={`withdraw-${paper.id}`} onClick={withdraw}>
            <XCircle className="w-3.5 h-3.5" /> Withdraw
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AuthorPanel() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = () => api.get("/my/papers").then(({ data }) => setPapers(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const visible = papers.filter((p) => {
    if (tab === "all") return true;
    if (tab === "closed") return ["rejected", "withdrawn"].includes(p.status);
    return p.status === tab;
  });

  const count = (k) => papers.filter((p) => (k === "all" ? true : k === "closed" ? ["rejected", "withdrawn"].includes(p.status) : p.status === k)).length;

  return (
    <div data-testid="author-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100">My Submissions</h1>
          <p className="text-slate-500 text-sm mt-1">Track every manuscript from submission to publication.</p>
        </div>
        <Button asChild className="rounded-full gap-1.5" data-testid="new-submission-btn">
          <Link to="/submit"><UploadCloud className="w-4 h-4" /> New Submission</Link>
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList className="flex-wrap h-auto">
          {TABS.map((t) => <TabsTrigger key={t.k} value={t.k} data-testid={`my-filter-${t.k}`}>{t.l} ({count(t.k)})</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-20 bg-card rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No submissions in this category.</p>
          {tab === "all" && <Button asChild className="mt-4 rounded-full"><Link to="/submit">Submit your first paper</Link></Button>}
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((p) => <SubmissionCard key={p.id} paper={p} reload={load} />)}
        </div>
      )}
    </div>
  );
}
